import { TechnologyCategory, TechnologyCategoryMapping, UpdateCategoryNameRequest } from '../models/technology-category.model';
import { BackendHttpService } from './backendHttpService';

export class TechnologyCategoryService extends BackendHttpService {
  private categoriesCache: TechnologyCategory[] | null = null;
  private mappingsCache: TechnologyCategoryMapping[] | null = null;

  constructor() {
    super('tech/categories');
  }

  async getCategories(): Promise<TechnologyCategory[]> {
    try {
      if (this.categoriesCache) {
        return this.categoriesCache;
      }
      
      this.categoriesCache = await this.get<TechnologyCategory[]>('');
      return this.categoriesCache;
    } catch (error) {
      console.error('Error getting technology categories:', error);
      throw error;
    }
  }

  async getCategoryMappings(): Promise<TechnologyCategoryMapping[]> {
    try {
      if (this.mappingsCache) {
        return this.mappingsCache;
      }
      
      this.mappingsCache = await this.get<TechnologyCategoryMapping[]>('/mappings');
      return this.mappingsCache;
    } catch (error) {
      console.error('Error getting technology category mappings:', error);
      throw error;
    }
  }

  async updateCategoryName(categoryId: number, newName: string): Promise<void> {
    try {
      const request: UpdateCategoryNameRequest = { newName };
      await this.put<UpdateCategoryNameRequest, void>(request, `/${categoryId}`);
      
      // Update cache if it exists
      if (this.categoriesCache) {
        this.categoriesCache = this.categoriesCache.map(category => 
          category.id === categoryId ? { ...category, name: newName } : category
        );
      }
    } catch (error) {
      console.error(`Error updating category ${categoryId}:`, error);
      throw error;
    }
  }

  async addTechnologyToCategory(categoryId: number, technologyId: number): Promise<void> {
    try {
      await this.post<void, void>(undefined, `/${categoryId}/technologies/${technologyId}`);
      
      // Clear mappings cache as it's now outdated
      this.clearMappingsCache();
    } catch (error) {
      console.error(`Error adding technology ${technologyId} to category ${categoryId}:`, error);
      throw error;
    }
  }

  async removeTechnologyFromCategory(categoryId: number, technologyId: number): Promise<void> {
    try {
      await this.delete(`/${categoryId}/technologies/${technologyId}`);
      
      // Clear mappings cache as it's now outdated
      this.clearMappingsCache();
    } catch (error) {
      console.error(`Error removing technology ${technologyId} from category ${categoryId}:`, error);
      throw error;
    }
  }

  clearCategoriesCache(): void {
    this.categoriesCache = null;
  }

  clearMappingsCache(): void {
    this.mappingsCache = null;
  }

  clearAllCache(): void {
    this.clearCategoriesCache();
    this.clearMappingsCache();
  }
} 